/**
 * Tipos públicos de NOTAMs — vista de API (no fila de BD).
 *
 * El tablón de NOTAMs (Fase 10) tiene **dos entidades distintas** que
 * comparten estética pero NO comparten datos ni endpoints:
 *
 *  - `Notam`: párrafos de la hoja oficial central (`papel.png`). Cada
 *    NOTAM es un párrafo suelto sin título; se renderizan en orden
 *    cronológico inverso. Solo admins pueden crearlos.
 *
 *  - `NotamPostit`: mensajes entre pilotos sobre el corcho. Cada uno
 *    tiene autor, opcional destinatario y posición libre (drag&drop).
 *    Cualquier piloto autenticado puede crear post-its.
 *
 * Ambos caducan a los 5 días desde su `publishedAt`. La caducidad se
 * filtra en runtime (no hay cron); el archivado manual de admins se
 * marca con `archived=true`.
 */

import type { Id, IsoTimestamp, Visibility, Callsign } from './primitives.js';

// ─── Hoja oficial ────────────────────────────────────────────────────────

/**
 * Párrafo de la hoja oficial de NOTAMs. Sin título — el orden de
 * publicación (más reciente arriba) hace de jerarquía visual.
 */
export interface Notam {
  id: Id;
  /** Texto del párrafo. Sin formato, sin título. */
  body: string;
  /** Autor (admin o superadmin). FK a `users.id`. */
  createdBy: Id;
  /** Callsign del autor joineado desde `users.callsign`. */
  authorCallsign: Callsign;
  publishedAt: IsoTimestamp;
  /** `publishedAt + 5 días`, calculado en el backend al crear. */
  expiresAt: IsoTimestamp;
  /** Archivado manualmente por un admin. Caducados se filtran en runtime. */
  archived: boolean;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Payload de POST y PATCH de NOTAMs. */
export interface NotamPayload {
  body?: string;
  archived?: boolean;
}

// ─── Post-its del corcho ─────────────────────────────────────────────────

/**
 * Mensaje colgado del corcho. Tres colores derivados de
 * `targetPilotId`/`visibility` (ver `notamPostItColor` abajo):
 *  - amarillo: público sin destinatario (`targetPilotId=null`)
 *  - rosa:     público con destinatario
 *  - azul:     privado a un piloto concreto
 */
export interface NotamPostit {
  id: Id;
  body: string;
  authorId: Id;
  authorCallsign: Callsign;
  /** Destinatario opcional. NULL = mensaje al escuadrón. */
  targetPilotId: Id | null;
  /** Callsign del destinatario joineado. NULL si `targetPilotId` lo es. */
  targetPilotCallsign: Callsign | null;
  /**
   * Con `targetPilotId=null` el visibility tiene que ser `'public'` (lo
   * valida el backend en POST/PATCH).
   */
  visibility: Visibility;
  /** Posición en el corcho, 0-100, relativa al ancho/alto del fondo. */
  xPercent: number;
  yPercent: number;
  publishedAt: IsoTimestamp;
  expiresAt: IsoTimestamp;
  archived: boolean;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Payload de POST y PATCH de post-its. */
export interface NotamPostitPayload {
  body?: string;
  targetPilotId?: Id | null;
  visibility?: Visibility;
  xPercent?: number;
  yPercent?: number;
  archived?: boolean;
}

// ─── Util de color ───────────────────────────────────────────────────────

export type NotamPostitColor = 'yellow' | 'blue' | 'pink';

/**
 * Color del post-it según destinatario y visibilidad. Misma función en
 * shared/ para que la pinte igual el frontend y la API la pueda derivar
 * cuando haga falta.
 */
export function notamPostItColor(
  postit: Pick<NotamPostit, 'targetPilotId' | 'visibility'>,
): NotamPostitColor {
  if (postit.targetPilotId === null) return 'yellow';
  if (postit.visibility === 'private') return 'blue';
  return 'pink';
}

// ─── Chincheta ───────────────────────────────────────────────────────────

/** Color de la chincheta visible arriba de cada post-it. */
export type NotamPostitPin = 'yellow' | 'blue' | 'green';

/**
 * Chincheta del post-it. Se elige al crearlo de forma "aleatoria pero
 * estable" — derivamos el color del `id` (UUID v4) con un hash trivial
 * para que el resultado no cambie entre renders y no necesitemos
 * persistir un campo extra en BD.
 *
 * No usamos un random puro en cada render porque eso haría que la
 * chincheta saltara de color al recargar; el dominio dice "al CREAR" se
 * elige una, así que la elección es estable por id.
 */
export function notamPostItPin(
  postit: Pick<NotamPostit, 'id'>,
): NotamPostitPin {
  // Hash sencillo: suma de char codes mod 3. UUID v4 tiene suficiente
  // entropía para distribuirse de forma aceptable entre 3 valores.
  let acc = 0;
  for (let i = 0; i < postit.id.length; i++) {
    acc = (acc + postit.id.charCodeAt(i)) % 1000003;
  }
  const pick = acc % 3;
  return pick === 0 ? 'yellow' : pick === 1 ? 'blue' : 'green';
}

// ─── Respuesta del tablón ────────────────────────────────────────────────

/**
 * Vista combinada que devuelve `GET /api/notams/board`. La API filtra:
 *  - solo NOTAMs no archivados y no caducados.
 *  - post-its no archivados, no caducados, y visibles para el caller
 *    (públicos siempre; privados solo si autor o destinatario).
 */
export interface NotamBoardResponse {
  notams: Notam[];
  postits: NotamPostit[];
}
